import { Link, useParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { JobStatusBadge } from "../components/JobStatusBadge.js";
import { ApiClientError } from "../lib/api.js";
import {
  canCancelResearchJob,
  canResumeResearchJob,
  cancelResearchJob,
  formatCostUsd,
  formatJobDate,
  getResearchJob,
  resumeResearchJob,
} from "../lib/researchJobs.js";

export function ResearchJobDetailPage() {
  const { jobId } = useParams();
  const queryClient = useQueryClient();

  const jobQuery = useQuery({
    queryKey: ["research-jobs", jobId],
    enabled: Boolean(jobId),
    queryFn: () => getResearchJob(jobId!),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "running" || status === "queued" || status === "cancelling"
        ? 5000
        : false;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelResearchJob(jobId!),
    onSuccess: (job) => {
      queryClient.setQueryData(["research-jobs", job.id], job);
      void queryClient.invalidateQueries({ queryKey: ["research-jobs"] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => resumeResearchJob(jobId!),
    onSuccess: (job) => {
      queryClient.setQueryData(["research-jobs", job.id], job);
      void queryClient.invalidateQueries({ queryKey: ["research-jobs"] });
    },
  });

  if (!jobId) {
    return <p className="text-sm text-red-800">Ungültige Job-ID.</p>;
  }

  if (jobQuery.isLoading) {
    return <p className="text-sm text-gray-500">Lade Job…</p>;
  }

  if (jobQuery.isError || !jobQuery.data) {
    return (
      <div className="rounded-lg border border-red-100 bg-red-100/40 px-4 py-3 text-sm text-red-800">
        {jobQuery.error instanceof ApiClientError
          ? jobQuery.error.message
          : "Job konnte nicht geladen werden."}
        <div className="mt-3">
          <Link to="/research-jobs" className="font-medium underline">
            Zurück zur Liste
          </Link>
        </div>
      </div>
    );
  }

  const job = jobQuery.data;
  const busy = cancelMutation.isPending || resumeMutation.isPending;
  const actionError =
    cancelMutation.error instanceof ApiClientError
      ? cancelMutation.error.message
      : resumeMutation.error instanceof ApiClientError
        ? resumeMutation.error.message
        : null;

  const criteriaEntries = Object.entries(job.criteria_json ?? {});

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <JobStatusBadge status={job.status} />
            <span className="text-xs text-gray-500">{job.current_stage}</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-800">{job.query}</h2>
          <p className="text-sm text-gray-500">{job.region}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCancelResearchJob(job.status) ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => cancelMutation.mutate()}
              className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-100/40 disabled:opacity-50"
            >
              Cancel
            </button>
          ) : null}
          {canResumeResearchJob(job.status) ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => resumeMutation.mutate()}
              className="rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Resume
            </button>
          ) : null}
          <Link
            to="/research-jobs"
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Zur Liste
          </Link>
        </div>
      </div>

      {actionError ? (
        <div className="rounded-lg border border-red-100 bg-red-100/40 px-4 py-3 text-sm text-red-800">
          {actionError}
        </div>
      ) : null}

      {job.error ? (
        <div className="rounded-lg border border-red-100 bg-red-100/40 px-4 py-3 text-sm text-red-800">
          Fehler: {job.error}
        </div>
      ) : null}

      <dl className="grid gap-4 rounded-xl border border-gray-200 bg-white p-5 sm:grid-cols-2">
        <Detail term="Zielanzahl" value={String(job.target_count)} />
        <Detail
          term="Kandidaten"
          value={`${job.candidates_processed} verarbeitet / ${job.candidates_found} gefunden`}
        />
        <Detail term="Leads qualifiziert" value={String(job.leads_qualified)} />
        <Detail
          term="Kosten (Schätzung)"
          value={formatCostUsd(job.cost_usd_estimate)}
        />
        <Detail term="Erstellt" value={formatJobDate(job.created_at)} />
        <Detail term="Gestartet" value={formatJobDate(job.started_at)} />
        <Detail term="Abgeschlossen" value={formatJobDate(job.completed_at)} />
        <Detail term="Heartbeat" value={formatJobDate(job.heartbeat_at)} />
        <Detail
          term="Workflow"
          value={job.workflow_instance_id ?? "—"}
        />
        <Detail
          term="Cancel requested"
          value={formatJobDate(job.cancel_requested_at)}
        />
      </dl>

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="mb-3 text-sm font-semibold text-gray-800">Kriterien</h3>
        {criteriaEntries.length === 0 ? (
          <p className="text-sm text-gray-500">Keine zusätzlichen Kriterien.</p>
        ) : (
          <ul className="space-y-1 text-sm text-gray-700">
            {criteriaEntries.map(([key, value]) => (
              <li key={key}>
                <span className="font-medium">{key}</span>:{" "}
                {typeof value === "boolean"
                  ? value
                    ? "ja"
                    : "nein"
                  : String(value)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Detail({ term, value }: { term: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
        {term}
      </dt>
      <dd className="mt-1 break-all text-sm text-gray-800">{value}</dd>
    </div>
  );
}
